import type { AgriTourFiles, AgriTourFormData, AgriTourStep } from './AgriTourScreen';
import { ITM_VALIDATION_MESSAGES, validateItmPair } from '@/lib/map/itm';

export type AgriTourValidationResult = {
  valid: boolean;
  fieldErrors: Record<string, string>;
  message: string;
};

function isEmpty(value: string | undefined): boolean {
  return !value?.trim();
}

function isPositiveNumber(value: string | undefined): boolean {
  if (isEmpty(value)) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}

function fail(
  fieldErrors: Record<string, string>,
  message: string,
): AgriTourValidationResult {
  return { valid: false, fieldErrors, message };
}

function ok(): AgriTourValidationResult {
  return { valid: true, fieldErrors: {}, message: '' };
}

export function validateAgriTourStep(
  step: AgriTourStep,
  formData: AgriTourFormData,
  files: AgriTourFiles,
): AgriTourValidationResult {
  const errors: Record<string, string> = {};
  const mark = (field: string, msg = 'שדה חובה') => {
    errors[field] = msg;
  };

  if (step === 'A') {
    if (isEmpty(formData.applicantName)) mark('applicantName');
    if (isEmpty(formData.idNumber)) mark('idNumber');
    if (isEmpty(formData.legalEntity)) mark('legalEntity');
    if (formData.legalEntity === '5' && isEmpty(formData.legalEntityOther)) {
      mark('legalEntityOther');
    }
    if (isEmpty(formData.settlementItemId)) mark('settlementItemId');
    if (isEmpty(formData.phone)) mark('phone');
    if (isEmpty(formData.email)) mark('email');
    if (isEmpty(formData.miluimActive)) mark('miluimActive');
    if (formData.miluimActive === '0' && !files.filezgshkkm2) {
      mark('filezgshkkm2', 'יש לצרף אישור משרת מילואים');
    }
    if (isEmpty(formData.ventureDescription)) mark('ventureDescription');
  }

  if (step === 'B') {
    if (!isPositiveNumber(formData.gushNumber)) mark('gushNumber');
    if (!isPositiveNumber(formData.helkaNumber)) mark('helkaNumber');
    if (isEmpty(formData.ownershipStatus)) mark('ownershipStatus');
    if (formData.ownershipStatus === '1' && isEmpty(formData.ownershipStatusOther)) {
      mark('ownershipStatusOther');
    }
    if (isEmpty(formData.parcelType)) mark('parcelType');
    if (isEmpty(formData.rentalAgreementVillage)) mark('rentalAgreementVillage');
    if (formData.rentalAgreementVillage === '0' && !files.filemk54lt2a) {
      mark('filemk54lt2a', 'יש לצרף הסכם שכירות');
    }
    if (isEmpty(formData.fiveYearAgreement)) mark('fiveYearAgreement');
    if (formData.fiveYearAgreement === '0' && !files.file4khnuexa) {
      mark('file4khnuexa', 'יש לצרף הסכם ל-5 שנים');
    }
    if (isEmpty(formData.statutoryMaturity)) mark('statutoryMaturity');
    if (formData.statutoryMaturity === '0' && !files.fileki8tq148) {
      mark('fileki8tq148', 'יש לצרף פטור מהיתר בנייה');
    }
    if (formData.leaseAgreementRMI === '0' && !files.filev6ow2t9a) {
      mark('filev6ow2t9a', 'יש לצרף הסכם חכירה');
    }
    if (formData.nearbyPlans === '0' && isEmpty(formData.environmentalPlansDetail)) {
      mark('environmentalPlansDetail');
    }
    if (formData.touristAnchors === '0' && isEmpty(formData.touristAnchorsDetail)) {
      mark('touristAnchorsDetail');
    }
    if (
      formData.selectionMode !== 'polygon_drawn' &&
      formData.lat.trim() &&
      formData.lng.trim() &&
      (formData.coordX.trim() || formData.coordY.trim())
    ) {
      const code = validateItmPair(Number(formData.coordX), Number(formData.coordY));
      if (code) {
        mark('coordX', ITM_VALIDATION_MESSAGES[code]);
      }
    }
  }

  if (step === 'C') {
    if (isEmpty(formData.ventureLocation)) mark('ventureLocation');
    if (isEmpty(formData.ventureActivityStatus)) mark('ventureActivityStatus');
    if (isEmpty(formData.readinessLevel)) mark('readinessLevel');
    if (isEmpty(formData.agriculturalIntegration)) mark('agriculturalIntegration');
    if (formData.agriculturalIntegration === '0' && isEmpty(formData.agriculturalDetail)) {
      mark('agriculturalDetail');
    }
    if (formData.buildingElements.length === 0) mark('buildingElements');
    if (!isPositiveNumber(formData.ventureAreaSqm)) mark('ventureAreaSqm');
    if (!isPositiveNumber(formData.numEmployees)) mark('numEmployees');
    if (isEmpty(formData.hasPhysicalInfrastructure)) mark('hasPhysicalInfrastructure');
    if (formData.requiredConnections.length === 0) mark('requiredConnections');
    if (isEmpty(formData.shadedGatheringArea)) mark('shadedGatheringArea');
    if (isEmpty(formData.sanitationSolution)) mark('sanitationSolution');
    if (isEmpty(formData.hasStatutoryAccessRoad)) mark('hasStatutoryAccessRoad');
    if (isEmpty(formData.accessibilityType)) mark('accessibilityType');
    if (isEmpty(formData.hasOrganizedParking)) mark('hasOrganizedParking');
    if (formData.hasOrganizedParking === '0' && !isPositiveNumber(formData.existingParkingSpots)) {
      mark('existingParkingSpots');
    }
    if (isEmpty(formData.needsNewParking)) mark('needsNewParking');
    if (formData.needsNewParking === '0') {
      if (!isPositiveNumber(formData.parkingCarsNeeded)) mark('parkingCarsNeeded');
      if (!isPositiveNumber(formData.parkingBusesNeeded)) mark('parkingBusesNeeded');
    }
    if (isEmpty(formData.seasonalOrPermanent)) mark('seasonalOrPermanent');
    if (formData.seasonalOrPermanent === '0' && isEmpty(formData.activitySeasons)) {
      mark('activitySeasons');
    }
    if (isEmpty(formData.activityHours)) mark('activityHours');
    if (isEmpty(formData.nightActivity)) mark('nightActivity');
    if (formData.nightActivity === '0' && isEmpty(formData.outdoorLighting)) {
      mark('outdoorLighting');
    }
    if (isEmpty(formData.amplificationSystem)) mark('amplificationSystem');
    if (!isPositiveNumber(formData.avgVisitorsPerDay)) mark('avgVisitorsPerDay');
    if (isEmpty(formData.simultaneousCapacity)) mark('simultaneousCapacity');
    if (isEmpty(formData.inboundTourism)) mark('inboundTourism');
    if (isEmpty(formData.additionalAudiences)) mark('additionalAudiences');
  }

  if (step === 'D') {
    if (files.file9djsb71e.length === 0) {
      mark('file9djsb71e', 'יש לצרף נספח חזותי');
    }
    if (!files.signaturecrvya9ht) mark('signaturecrvya9ht', 'נדרשת חתימה');
  }

  if (Object.keys(errors).length > 0) {
    const count = Object.keys(errors).length;
    return fail(
      errors,
      `לא ניתן להמשיך — ${count === 1 ? 'שדה חובה אחד לא מלא' : `${count} שדות חובה לא מלאים`}. אנא מלאו את השדות המסומנים.`,
    );
  }

  return ok();
}
